/** @format */

import React, { useEffect, useState, useCallback } from "react"
import { PagingContainer, Container } from "./CreditDispatchTable.styled"
import { AutoModeUpdate, CreditDispatchTableProps, DistributionUpdate, TIMEOUT_DURATION, UpdateInProgressEnum } from "./CreditDispatchTable.types"
import { Paging } from "@ui-library/core"
import Table from "_components/atoms/Table/Table"
import bffClient, { MemberCreditDetailsRequest, MembersSelectionModeRequest, SelectionModeType } from "_clients/BFFClient"
import { Distribution, useDistributionStore } from "../../../stores/distribution/distribution"
import { getProductTypeEnumFromId } from "_components/organisms/Dashboard/Dashboard.types"
import { useAvailableBoostStore } from "_stores/availableBoost/availableBoost"
import useNetworkMarketsData from "_shared/hooks/useNetworkMarketsData"
import useTimeoutEffect from "_shared/hooks/useTimeoutEffect"
import { DashboardParamsType } from "_components/organisms/CreditDispatchDashboard/CreditDispatchDashboard.types"
import useCreditDispatch from "_shared/hooks/creditDispatch/useCreditDispatch"
import { useColumns } from "_shared/hooks/creditDispatch/useColumns"

type InnerProps = CreditDispatchTableProps

const CreditDispatchTable = (props: InnerProps) => {
	const { pageNumber, data, networkId, productType, marketId, contractId, setDistributionChangeInProgress, onPageChange, isLoading } = props
	const { distributions, findDistribution, upsertDistributions, removeOutdatedDistributions } = useDistributionStore();
	const { availableBoosts, upsertAvailableBoost, findAvailableBoost, removeOutdatedAvailableBoosts } = useAvailableBoostStore()
	const { networkMarkets } = useNetworkMarketsData(networkId, contractId)
	const { compareAndUpdateDistributions, members } = useCreditDispatch(data.members, marketId, productType, setDistributionChangeInProgress, networkId, contractId);
	const [distribUpdate, setDistribUpdate] = useState<DistributionUpdate>()
	const [automodeUpdate, setAutomodeUpdate] = useState<AutoModeUpdate>()
	const [updateInProgress, setUpdateInProgress] = useState<UpdateInProgressEnum>(UpdateInProgressEnum.None)

	const product = networkMarkets?.find(m => m.id == marketId)?.products?.find(p => productType == getProductTypeEnumFromId(p.id.toString()))
	const contract = product?.contracts?.find(c => c.contractId == contractId);

	const [isEligibleAutoSelection, assignedNotUsedCreditsCount, availableCreditsCount] = networkId 
		? [product?.isEligibleAutoSelection, product?.totalAssignedNotUsedBoosts, product?.totalAvailableBoosts] 
		: [false, contract?.assignedNotUsed, contract?.available];

	useEffect(() => {
		removeOutdatedDistributions();
		removeOutdatedAvailableBoosts();
	}, [networkId, productType, marketId, contractId, pageNumber])

	const updateRepartitionStarted = UpdateInProgressEnum.UpdateRepartitionInProgress === (UpdateInProgressEnum.UpdateRepartitionInProgress & updateInProgress)
	const updateModeAutoStarted = UpdateInProgressEnum.UpdateAutoModeInProgress == (UpdateInProgressEnum.UpdateAutoModeInProgress & updateInProgress)
	
	const searchParams : DashboardParamsType = { networkId, marketId, productType, contractId };

	const handleAutoModeOnChange = (value: boolean, rcuId: string) => {
		setAutomodeUpdate(prev => {
			const key = `${rcuId}-${networkId}-${marketId}-${productType}`
			return {
				...prev,
				[key]: {
					rcuId: rcuId,
					selectionModeType: value ? SelectionModeType.Auto: SelectionModeType.Manual,
					networkId: networkId,
					marketId: marketId,
					productType: productType,
				},
			}
		})
	}

	const setGenericUpdateInProgress = (type: UpdateInProgressEnum) => (actif: boolean) => {
		setUpdateInProgress(currentValue => {
			let value: UpdateInProgressEnum = currentValue
			if (actif) {
				value |= type
			} else {
				value &= ~type
			}
			return value
		})
	}

	const setUpdateRepartitionStarted = setGenericUpdateInProgress(UpdateInProgressEnum.UpdateRepartitionInProgress)
	const setUpdateModeAutoStarted = setGenericUpdateInProgress(UpdateInProgressEnum.UpdateAutoModeInProgress)

	const handleOnChange = (value: number, rcuId: string, previousValue: number) => {
		const total = findAvailableBoost(networkId, contractId, Number(marketId), productType)?.newTotalAvailableBoosts ?? availableCreditsCount
		const isValueValid = total - (value - previousValue) >= 0

		if (isValueValid) {
			setDistribUpdate(prevDistribUpdate => {
				const key = `${rcuId}-${networkId}-${marketId}-${productType}`
				return {
					...prevDistribUpdate,
					[key]: {
						rcuId,
						value,
						networkId,
						marketId,
						productType,
					},
				}
			})
		}
	}

	const performUpdateRepartition = async() => {
		setUpdateRepartitionStarted(true)
		const updateRequests = Object.values(distribUpdate).map(d =>
			({ allocatedCredits: d.value, rcuId: d.rcuId } as MemberCreditDetailsRequest))

		let incomingUpsertedDistributions = []
		const availableBoost = findAvailableBoost(networkId, contractId, Number(marketId), productType)
		let newTotalAvailableCredits = availableBoost?.newTotalAvailableBoosts ?? availableCreditsCount
		let newDistributedButNotUsed = availableBoost?.newDistributedNotUsedBoosts ?? assignedNotUsedCreditsCount
		const currentDate = new Date()
		for (const request of updateRequests) {
			const previousDistribution = findDistribution(networkId, contractId, request.rcuId, Number(marketId), productType)
				?? data.members.find(m => m.rcuID == request.rcuId)

			const previousTotalAllocatedBoosts = 'newAllocatedBoosts' in previousDistribution 
				? previousDistribution.newAllocatedBoosts 
				: data.members.find(m => m.rcuID == request.rcuId).allocatedBoosts
			
			newTotalAvailableCredits -= (request.allocatedCredits - previousTotalAllocatedBoosts)
			newDistributedButNotUsed += (request.allocatedCredits - previousTotalAllocatedBoosts)
			incomingUpsertedDistributions.push({...previousDistribution, rcuId: request.rcuId, newAllocatedBoosts: request.allocatedCredits, 
				networkId: networkId, contractId: contractId, marketId: marketId, productType: productType, addDate: currentDate})

			if (newTotalAvailableCredits < 0 ) {
				// reset incoming distribution
				setDistribUpdate(undefined)
				setUpdateRepartitionStarted(false)
				return
			}
    	}

		if (newTotalAvailableCredits < 0 ) {
			// reset incoming distribution
			setDistribUpdate(undefined)
			setUpdateRepartitionStarted(false)
			return;
		}
		if (newTotalAvailableCredits >= 0) {
			try {
				const response = networkId
					? await bffClient.patchNetworkMemberDistribution(networkId, productType, marketId, undefined, updateRequests)
					: await bffClient.patchMultiBeneficiariesMemberDistribution(contractId, productType, marketId, undefined, updateRequests)

				if (response.status == 200) {
					upsertAvailableBoost({ networkId: networkId, contractId: contractId, marketId: Number(marketId), productType: productType,
						newTotalAvailableBoosts: newTotalAvailableCredits, newDistributedNotUsedBoosts: newDistributedButNotUsed, addDate: currentDate,
					})
					setDistribUpdate(undefined)
					setUpdateRepartitionStarted(false)
					upsertDistributions(incomingUpsertedDistributions)
				}
			} catch (error) {
				setUpdateRepartitionStarted(false)
			}
		}
	}

	const performAutomode = async () => {
		setUpdateModeAutoStarted(true);

		let incomingUpsertedAutoMode = []

		const automodeRequests = Object.values(automodeUpdate).map(d => ({
			selectionModeType: d.selectionModeType,
			rcuId: d.rcuId,
		}));

		const currentDate = new Date();

		let requests: MembersSelectionModeRequest[] = [
			{
				rcuIds: automodeRequests.filter(r => r.selectionModeType === SelectionModeType.Manual).map(r => r.rcuId),
				selectionMode: SelectionModeType.Manual
			},
			{
				rcuIds: automodeRequests.filter(r => r.selectionModeType === SelectionModeType.Auto).map(r => r.rcuId),
				selectionMode: SelectionModeType.Auto
			}
		].filter(request => request.rcuIds.length > 0);

		try {
			await bffClient.multiToggleMembersSelectionMode(networkId,productType, marketId, undefined, requests);

			automodeRequests.forEach(request => {
				const distribution = findDistribution(networkId, contractId, request.rcuId, Number(marketId), productType) 
					?? data.members.find(m => m.rcuID == request.rcuId);

				const newDistribution: Distribution = {
					...distribution,
					rcuId: request.rcuId,
					networkId: networkId,
					contractId: contractId,
					marketId: marketId,
					productType: productType,
					addDate: currentDate,
					selectionModeType: request.selectionModeType
				}
				incomingUpsertedAutoMode.push(newDistribution);
			});
			setAutomodeUpdate(undefined);
			upsertDistributions(incomingUpsertedAutoMode);
		}
		catch { }
		setUpdateModeAutoStarted(false);
	}

	useTimeoutEffect(distribUpdate, performUpdateRepartition, [distribUpdate], TIMEOUT_DURATION);
	useTimeoutEffect(automodeUpdate, performAutomode, [automodeUpdate], TIMEOUT_DURATION);

	const onClick = useCallback((page: number) => onPageChange(page), [onPageChange])

	useEffect(() => compareAndUpdateDistributions(),
		[distributions, networkId, marketId, productType, updateRepartitionStarted, updateModeAutoStarted, data, availableBoosts])

	const columns = useColumns(networkMarkets, isLoading, updateRepartitionStarted, updateModeAutoStarted, isEligibleAutoSelection, searchParams,
		distribUpdate, automodeUpdate, handleOnChange, handleAutoModeOnChange, [distributions, updateRepartitionStarted, updateModeAutoStarted, isLoading, networkMarkets, availableBoosts]);

	const genericTableProps = {columns: columns, data: members, colsChoiceEnabled: true, onClickColsChoice: () => {},
		rowProps: _ => {
			if (props.data.members.length) {
				return { onClick: () => {} }
			}
		},
		testid: "credit-dispatch-table",
	}

	return (
		<Container>
			<Table isLoading={isLoading} {...genericTableProps} />
			<PagingContainer>
				{!isLoading &&
					<Paging
						current={pageNumber}
						itemCountPerPage={data.pageSize}
						linkElement="a"
						onClick={onClick}
						totalCount={data.total}
						pageInfoCustomLabel={({ itemFrom, itemTo, totalCount }) => (
							<>
								{`Membres `}<b>{itemFrom} à {itemTo} </b>{`sur ${totalCount}`}
							</>
						)}
					/>
				}
			</PagingContainer>
		</Container>
	)
}

export default CreditDispatchTable
